if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          mediaBars: this.querySelector('[data-muori-media-bars]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // Muori: on phones the pictures sit side by side; a swipe moves one
        // picture, following the finger, and the ends join up
        // (muori-loop-slider.js). The
        // arrows over the picture (snippets/product-media-gallery.liquid) step
        // along it. Only where the arrows are drawn: not for a single picture,
        // nor where the gallery is cut to one (`limit: 1`). Not in the
        // quick-add modal either, which shows its first picture only and hides
        // the arrows.
        const arrows = this.querySelectorAll('[data-muori-media-step]');
        arrows.forEach((button) => {
          button.addEventListener('click', () => this.showAdjacentMedia(Number(button.dataset.muoriMediaStep)));
        });
        if (arrows.length && !this.closest('quick-add-modal')) {
          // A section higher up the page can load this file before
          // muori-loop-slider.js has run. Deferred scripts have all run by
          // DOMContentLoaded.
          if (window.MuoriLoopSlider || document.readyState !== 'loading') {
            this.setUpLoop();
          } else {
            document.addEventListener('DOMContentLoaded', () => this.setUpLoop(), { once: true });
          }
        }

        if (!this.elements.thumbnails && !this.elements.mediaBars) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails?.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.elements.viewer.addEventListener('click', this.onMediaBarClick.bind(this));
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      setUpLoop() {
        const list = this.elements.viewer?.querySelector('.product__media-list');
        if (!list || !window.MuoriLoopSlider) return;

        this.loop = new MuoriLoopSlider(list, {
          onSettle: (index) => {
            if (!this.mql.matches) this.markActive(this.loop.items[index]);
          },
        });
        // Turning a phone sideways can cross into the PC layout, which shows
        // only the active picture. Turning it back, the slider starts again
        // from that picture.
        this.mql.addEventListener('change', () => {
          if (this.mql.matches) return;
          const index = this.loop.items.findIndex((item) => item.classList.contains('is-active'));
          this.loop.go(Math.max(index, 0), false);
        });
      }

      refreshMediaBars() {
        this.elements.mediaBars = this.querySelector('[data-muori-media-bars]');
      }

      // Stepping is choosing the neighbour of the picture in view. Past either
      // end it wraps round, as the client's reference does. Only the `li`s: the
      // zoom button and the 3D model inside each carry `data-media-id` too.
      // On phones the loop moves the slider, through the copy at the end when
      // it wraps, so the state is set here without setActiveMedia's own scroll.
      showAdjacentMedia(step) {
        if (this.loop && !this.mql.matches) {
          this.markActive(this.loop.items[this.loop.step(step)]);
          return;
        }

        const items = Array.from(this.elements.viewer.querySelectorAll('li[data-media-id]'));
        if (items.length < 2) return;
        const current = items.findIndex((item) => item.classList.contains('is-active'));
        const target = items[(current + step + items.length) % items.length];
        this.setActiveMedia(target.dataset.mediaId, false);
      }

      // Muori: what setActiveMedia does to the state, without the scrolling.
      // On phones the slider has already moved, or is moving, by itself.
      markActive(item) {
        if (!item) return;
        this.elements.viewer.querySelectorAll('li[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === item);
        });
        this.refreshMediaBars();
        const mediaId = item.dataset.mediaId;
        this.setActiveThumbnail(this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`));
        this.setActiveMediaBar(this.elements.mediaBars?.querySelector(`[data-target="${mediaId}"]`));
      }

      onMediaBarClick(event) {
        const mediaToSwitch = event.target.closest('[data-muori-media-bars] [data-target]');
        if (!mediaToSwitch) return;

        this.setActiveMedia(mediaToSwitch.dataset.target, false);
      }

      onSlideChanged(event) {
        // Muori: with the loop on phones, Dawn's page count is off by the copy
        // at the front, and the loop's onSettle keeps the state instead.
        if (!event.detail.currentElement || (this.loop && !this.mql.matches)) return;
        this.refreshMediaBars();
        const thumbnail = this.elements.thumbnails?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        const mediaBar = this.elements.mediaBars?.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveMediaBar(mediaBar);
      }

      setActiveMedia(mediaId, prepend) {
        this.refreshMediaBars();
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.mediaBars) {
            const activeMediaBar = this.elements.mediaBars.querySelector(`[data-target="${mediaId}"]`);
            if (activeMediaBar) {
              activeMediaBar.parentElement.firstChild !== activeMediaBar && activeMediaBar.parentElement.prepend(activeMediaBar);
            }
          }

          // Muori: the picture just moved to the front went in ahead of the
          // loop's copy there. Put the copies back round the new order, before
          // Dawn measures the slides again.
          this.loop?.refresh(0);
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          // Muori: on phones the loop moves the slider, the same way a swipe
          // does (muori-loop-slider.js; the list has no smooth scrolling).
          if (this.loop && !this.mql.matches) {
            this.loop.go(this.loop.items.indexOf(activeMedia));
          } else if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails && !this.elements.mediaBars) return;
        const activeThumbnail = this.elements.thumbnails?.querySelector(`[data-target="${mediaId}"]`);
        const activeMediaBar = this.elements.mediaBars?.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.setActiveMediaBar(activeMediaBar);
        const activePosition = activeThumbnail?.dataset.mediaPosition || activeMediaBar?.dataset.mediaPosition;
        if (activePosition) this.announceLiveRegion(activeMedia, activePosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      setActiveMediaBar(mediaBar) {
        if (!this.elements.mediaBars || !mediaBar) return;

        this.elements.mediaBars
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        mediaBar.setAttribute('aria-current', true);
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
